/**
 * Payslip Service
 *
 * Service for managing payslips and payroll data from Business Central API
 */

import { bcClient } from './businessCentralClient';
import { BC_CONFIG } from './config';

/**
 * Generate payslip in Base64 format
 * @param {string} payPeriodCode - Pay period code (e.g., 'PP00002')
 * @param {string} employeeNo - Employee number (e.g., '800008')
 * @param {string} token - Optional OAuth token (will fetch if not provided)
 * @returns {Promise<Object>} Payslip data with base64 content
 */
export async function generatePayslipBase64(payPeriodCode, employeeNo, token = null) {
  try {
    // Get token if not provided
    if (!token) {
      const tokenData = await bcClient.getToken();
      token = tokenData.access_token;
    }

    // Encode single quotes in the URL parameters
    const encodedPeriod = encodeURIComponent(`'${payPeriodCode}'`);
    const encodedEmployee = encodeURIComponent(`'${employeeNo}'`);

    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/payslipGenerators(${encodedPeriod},${encodedEmployee})/Microsoft.NAV.GeneratePayslipBase64`;

    console.log(`Generating payslip for Employee: ${employeeNo}, Pay Period: ${payPeriodCode}`);
    console.log(`Endpoint: ${endpoint}`);

    const response = await bcClient.post(endpoint, token, {});

    return response;
  } catch (error) {
    console.error('Error generating payslip:', error);
    throw error;
  }
}

/**
 * Get posted payroll lines
 * @param {string} token - Optional OAuth token (will fetch if not provided)
 * @param {Object} filters - Query filters (e.g., { employeeNo, payPeriod })
 * @returns {Promise<Array>} List of posted payroll lines
 */
export async function getPostedPayrollLines(token = null, filters = {}) {
  try {
    // Get token if not provided
    if (!token) {
      const tokenData = await bcClient.getToken();
      token = tokenData.access_token;
    }

    // Build OData filter properly (combine multiple filters with 'and')
    const filterParts = [];
    if (filters.employeeNo) {
      filterParts.push(`EmployeeNo eq '${filters.employeeNo}'`);
    }
    if (filters.payPeriod) {
      filterParts.push(`PayPeriod eq '${filters.payPeriod}'`);
    }
    if (filters.fiscalYear) {
      filterParts.push(`fiscalYear eq '${filters.fiscalYear}'`);
    }

    // Build query parameters
    const queryParams = [];
    if (filterParts.length > 0) {
      queryParams.push(`$filter=${filterParts.join(' and ')}`);
    }
    if (filters.$top) {
      queryParams.push(`$top=${filters.$top}`);
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/postedPayrollLines${queryString}`;

    console.log('Fetching posted payroll lines from:', endpoint);

    const response = await bcClient.get(endpoint, token);

    return response.value || response;
  } catch (error) {
    console.error('Error fetching posted payroll lines:', error);
    throw error;
  }
}

/**
 * Get payslip for logged-in employee
 * Convenience method that uses employee context
 * @param {string} employeeNo - Employee number
 * @param {string} payPeriodCode - Pay period code
 * @returns {Promise<Object>} Payslip data
 */
export async function getMyPayslip(employeeNo, payPeriodCode) {
  try {
    console.log(`Fetching payslip for current employee: ${employeeNo}`);
    return await generatePayslipBase64(payPeriodCode, employeeNo);
  } catch (error) {
    console.error('Error fetching my payslip:', error);
    throw error;
  }
}

/**
 * Decode base64 payslip to downloadable PDF
 * @param {string} base64Data - Base64 encoded payslip
 * @param {string} filename - Filename for download (default: 'payslip.pdf')
 * @returns {void} Triggers browser download
 */
export function downloadPayslipPDF(base64Data, filename = 'payslip.pdf') {
  try {
    // Remove data URL prefix if present
    const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

    // Convert base64 to blob
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    console.log(`✅ Payslip downloaded: ${filename}`);
  } catch (error) {
    console.error('Error downloading payslip:', error);
    throw error;
  }
}

/**
 * Open payslip PDF in new tab
 * @param {string} base64Data - Base64 encoded payslip
 * @returns {void} Opens PDF in new tab
 */
export function openPayslipPDF(base64Data) {
  try {
    // Remove data URL prefix if present
    const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

    // Convert to blob URL
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    // Open in new tab
    window.open(url, '_blank');

    console.log('✅ Payslip opened in new tab');
  } catch (error) {
    console.error('Error opening payslip:', error);
    throw error;
  }
}

/**
 * Get employee payroll elements (compensation details)
 * @param {string} employeeCode - Employee code
 * @param {string} token - Optional OAuth token (will fetch if not provided)
 * @returns {Promise<Array>} List of payroll elements
 */
export async function getEmployeePayrollElements(employeeCode, token = null) {
  try {
    // Get token if not provided
    if (!token) {
      const tokenData = await bcClient.getToken();
      token = tokenData.access_token;
    }

    const endpoint = `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/employeePayrollElements?$filter=employeeCode eq '${employeeCode}'`;

    console.log(`Fetching payroll elements for employee: ${employeeCode}`);
    console.log(`Endpoint: ${endpoint}`);

    const response = await bcClient.get(endpoint, token);

    return response.value || response;
  } catch (error) {
    console.error('Error fetching employee payroll elements:', error);
    throw error;
  }
}

// Export service object
export const payslipService = {
  generatePayslipBase64,
  getPostedPayrollLines,
  getMyPayslip,
  downloadPayslipPDF,
  openPayslipPDF,
  getEmployeePayrollElements,
};

export default payslipService;
